/**
 * Camera grammar (ART-BIBLE §8).
 *
 * Rules encoded here rather than left to discipline:
 *  · cuts are banned — every transition is a move;
 *  · the breathing idle never stops (a perfectly still camera makes a diorama look dead);
 *  · the camera never rolls except in `spectacle`;
 *  · the dolly-zoom is reserved for entering a machine, so it keeps meaning.
 */

import { PerspectiveCamera, Vector3, MathUtils } from 'three';
import { damp, shake, DRIFT, DOLLY, lerp } from '../anim/tween';

export type CamMode = 'aisle' | 'cabinet' | 'board' | 'spectacle' | 'peek';

interface ModeSpec { fov: number; height: number; dist: number; pitch: number }

const MODES: Record<CamMode, ModeSpec> = {
  aisle: { fov: 38, height: 19, dist: 52, pitch: -0.14 },
  cabinet: { fov: 30, height: 13, dist: 34, pitch: -0.05 },
  board: { fov: 30, height: 14, dist: 17, pitch: -0.10 },
  spectacle: { fov: 44, height: 16, dist: 44, pitch: -0.07 },
  peek: { fov: 38, height: 19, dist: 52, pitch: -0.12 },
};

export interface RigOpts { camera: PerspectiveCamera }

export class CameraRig {
  camera: PerspectiveCamera;
  mode: CamMode = 'aisle';
  /** where the rig wants to look */
  target = new Vector3(0, 12, -14);
  /** where the rig wants to stand */
  private want = new Vector3(0, 19, 38);
  private posNow = new Vector3(0, 19, 38);
  private lookNow = new Vector3(0, 12, -14);
  private fovWant = 38;
  private yawOffset = 0;
  private yawWant = 0;
  private rollWant = 0;
  private rollNow = 0;
  private kickT = 999;
  private kickAmp = 0;
  private time = 0;
  /** lambda: how hard the rig chases. Raised briefly for dolly moves. */
  private lambda = 2.2;
  private lambdaDecayTo = 2.2;
  /**
   * True while a *player-initiated* move owns the rig (they walked into a machine).
   * Ambient camera scenery checks this and yields. Cleared by going back to the aisle.
   */
  private playerHold = false;

  constructor(opts: RigOpts) {
    this.camera = opts.camera;
    this.apply(0.016);
  }

  private spec(): ModeSpec { return MODES[this.mode]; }

  /** walk the aisle: stand back and look along it */
  toAisle(x: number, z = -14): this {
    this.playerHold = false;   // back in the room; scenery may have the camera again
    this.mode = 'aisle';
    const s = MODES.aisle;
    this.target.set(x, 12, z);
    this.want.set(x, s.height, z + s.dist);
    this.fovWant = s.fov;
    this.yawWant = 0;
    this.rollWant = 0;
    this.push();
    return this;
  }

  /**
   * The signature move: dolly in while the fov narrows. Entering a machine, only.
   * `dist` scales with the cabinet so a 26-unit pachinko and an 11-unit roulette table
   * both fill the same fraction of frame — framing is a property of the subject.
   */
  toCabinet(focus: Vector3, opts: { dist?: number; lift?: number; yaw?: number; mode?: CamMode } = {}): this {
    this.playerHold = true;    // the player's hands outrank any ambient drift
    this.mode = opts.mode ?? 'cabinet';
    const s = this.spec();
    const d = opts.dist ?? s.dist;
    const yaw = opts.yaw ?? 0;
    this.target.copy(focus);
    this.want.set(
      focus.x + Math.sin(yaw) * d,
      focus.y + (opts.lift ?? s.height * 0.25),
      focus.z + Math.cos(yaw) * d,
    );
    this.fovWant = s.fov;
    this.yawWant = 0;
    this.rollWant = 0;
    this.push();
    return this;
  }

  /** free placement, for fixtures that compose their own shot */
  place(pos: Vector3, look: Vector3, fov = 34): this {
    this.mode = 'aisle';
    this.want.copy(pos);
    this.target.copy(look);
    this.fovWant = fov;
    this.yawWant = 0;
    this.rollWant = 0;
    this.push();
    return this;
  }

  /** rack onto the Hausregeln board — the room is the character sheet */
  toBoard(boardPos: Vector3): this {
    this.mode = 'board';
    const s = MODES.board;
    this.target.copy(boardPos);
    this.want.set(boardPos.x - s.dist * 0.55, boardPos.y + 1.2, boardPos.z + s.dist * 0.8);
    this.fovWant = s.fov;
    this.rollWant = 0;
    this.push();
    return this;
  }

  /** the money shot: pull back 12%, drop 4 degrees, 2.5 degrees of roll */
  toSpectacle(focus: Vector3): this {
    this.mode = 'spectacle';
    const s = MODES.spectacle;
    this.target.copy(focus);
    this.want.set(focus.x, focus.y + 4, focus.z + s.dist);
    this.fovWant = s.fov;
    this.rollWant = MathUtils.degToRad(2.5);
    this.push();
    return this;
  }

  /** VERDACHT: yaw six degrees to reveal Der Croupier, hold a BEAT, come back */
  peek(dir = 1): this {
    this.yawWant = MathUtils.degToRad(6) * dir;
    return this;
  }
  unpeek(): this { this.yawWant = 0; return this; }

  /** a hit: cabinet kicks, glass bursts, the bouncer leans on the machine */
  kick(amp = 0.5): this { this.kickT = 0; this.kickAmp = amp; return this; }

  private push(): void { this.lambda = 8.5; this.lambdaDecayTo = 2.6; }

  /** instant placement, for screenshot gates that must be deterministic */
  snap(): this {
    this.posNow.copy(this.want);
    this.lookNow.copy(this.target);
    this.camera.fov = this.fovWant;
    this.rollNow = this.rollWant;
    this.yawOffset = this.yawWant;
    this.apply(0.016);
    return this;
  }

  update(dt: number): void {
    this.time += dt;
    this.lambda = damp(this.lambda, this.lambdaDecayTo, 3.0, dt);
    this.posNow.x = damp(this.posNow.x, this.want.x, this.lambda, dt);
    this.posNow.y = damp(this.posNow.y, this.want.y, this.lambda, dt);
    this.posNow.z = damp(this.posNow.z, this.want.z, this.lambda, dt);
    this.lookNow.x = damp(this.lookNow.x, this.target.x, this.lambda, dt);
    this.lookNow.y = damp(this.lookNow.y, this.target.y, this.lambda, dt);
    this.lookNow.z = damp(this.lookNow.z, this.target.z, this.lambda, dt);
    this.camera.fov = damp(this.camera.fov, this.fovWant, this.lambda * 0.9, dt);
    this.yawOffset = damp(this.yawOffset, this.yawWant, 4.0, dt);
    this.rollNow = damp(this.rollNow, this.rollWant, 3.2, dt);
    if (this.kickT < 0.6) this.kickT += dt;
    this.apply(dt);
  }

  private apply(_dt: number): void {
    const c = this.camera;
    // the breathing idle: ±0.6 degrees of yaw, ±1.2 cm of rise, never stops
    const br = this.time * (Math.PI * 2 / DRIFT);
    const breatheYaw = Math.sin(br) * MathUtils.degToRad(0.6);
    const breatheY = Math.sin(br * 1.31) * 0.15;
    const k = shake(this.kickT, 0.6, this.kickAmp);

    const yaw = this.yawOffset + breatheYaw;
    const dx = this.posNow.x - this.lookNow.x;
    const dz = this.posNow.z - this.lookNow.z;
    const cs = Math.cos(yaw), sn = Math.sin(yaw);
    c.position.set(
      this.lookNow.x + dx * cs - dz * sn + k * 0.6,
      this.posNow.y + breatheY + k,
      this.lookNow.z + dx * sn + dz * cs,
    );
    c.up.set(Math.sin(this.rollNow), Math.cos(this.rollNow), 0);
    c.lookAt(this.lookNow.x, this.lookNow.y + k * 0.4, this.lookNow.z);
    c.updateProjectionMatrix();
  }

  /** slow aisle dolly, for the establishing shot */
  /**
   * The ambient aisle drift (the floor transition: Der Croupier wheeling the cabinets
   * down a level). It is *scenery*, and scenery must never outrank the player's hands.
   *
   * This used to override the rig unconditionally, which produced a bug that read as
   * "the game is broken" rather than as a camera issue: `floorStart` fires the moment a
   * floor begins — including floor 1, in the RunDirector's constructor — and its drift
   * runs for BEAT*4 = 2 s, forcing `mode = 'aisle'` and rewriting the target every frame.
   * Walk up to a machine inside that window and the dolly-zoom is silently stomped: you
   * are standing at the pachinko board, the Hausregeln board says KUGELREGEN, and the
   * camera is looking at the claw machine two cabinets away. Measured, not guessed —
   * entering pachinko at boot left the rig at `mode=aisle, target.x=24` (the drift's own
   * endpoint) while pachinko stands at x=-46.
   *
   * That is once at the start of every run and once per floor, six times a run.
   */
  driftAlong(x0: number, x1: number, u: number, z = -14): void {
    if (this.playerHold) return;
    this.mode = 'aisle';
    const s = MODES.aisle;
    const x = lerp(x0, x1, u);
    this.target.set(x, 12, z);
    this.want.set(x, s.height, z + s.dist);
    this.fovWant = s.fov;
  }
}

export { DOLLY };
